import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  serverTimestamp,
  runTransaction,
  query,
  where,
} from "firebase/firestore";

import { db } from "../firebase";

const INVENTORY_SUMMARY =
  "inventory_summary";

const INVENTORY_TRANSACTIONS =
  "inventory_transactions";

const SITE_INVENTORY_SUMMARY =
  "site_inventory_summary";

/* ==================================================
   MOVEMENT TYPES
================================================== */

export const MOVEMENT_TYPES = {

  GRN_IN:
    "GRN_IN",

  MR_ISSUE:
    "MR_ISSUE",

  DIRECT_ISSUE:
    "DIRECT_ISSUE",

  RETURN_UNUSED:
    "RETURN_UNUSED",

  RETURN_DEFECTIVE:
    "RETURN_DEFECTIVE",

  ADJUSTMENT:
    "ADJUSTMENT",

  VENDOR_RETURN:
    "VENDOR_RETURN",

  SCRAP:
  "SCRAP",

STOCK_TRANSFER_OUT:
  "STOCK_TRANSFER_OUT",

STOCK_TRANSFER_IN:
  "STOCK_TRANSFER_IN",

// Site-to-site transfer (Phase 2) — deliberately distinct from
// STOCK_TRANSFER_OUT/IN above, which only tag a central-inventory
// relocation and never touch site_inventory_summary.
SITE_TRANSFER_OUT:
  "SITE_TRANSFER_OUT",

SITE_TRANSFER_IN:
  "SITE_TRANSFER_IN",

SITE_TRANSFER_REVERSED:
  "SITE_TRANSFER_REVERSED",

// Direct Site Purchase (Priority 4) — GRN received directly at a
// project's site, never touching central inventory_summary. Distinct
// from GRN_IN (always credits central stock) and SITE_TRANSFER_IN
// (moves stock that already existed in site_inventory_summary) — this
// is the ORIGIN entry for material that never passed through Store.
SITE_GRN_IN:
  "SITE_GRN_IN",
};
export const transferStock = async ({
  company_id,
  item_id,
  item_code,
  item_name,
  category,
  qty,
  unit,
  from_location,
  to_location,
  approved_by,
  remarks = "",
}) => {

  try {

    validateItemIdentity({
      item_id
    });

    const finalQty =
      Number(qty || 0);

    if (finalQty <= 0) {

      throw new Error(
        "Valid transfer qty required"
      );
    }

    const validation =
      await validateAvailableStock(
        item_id,
        finalQty,
        company_id || ""
      );

    if (!validation.success) {

      throw new Error(
        validation.message
      );
    }

    // =====================================
    // DEDUCT CENTRAL STOCK
    // =====================================

    await updateInventorySummary({

      company_id,

      item_id,

      item_code,

      item_name,

      category,

      unit,

      stock_change:
        -finalQty,
    });

    // =====================================
    // OUT ENTRY
    // =====================================

    await createInventoryTransaction({

      company_id,

      movement_type:
        MOVEMENT_TYPES.STOCK_TRANSFER_OUT,

      item_id,

      item_code,

      item_name,

      category,

      qty:
        finalQty,

      unit,

      stock_effect:
        -finalQty,

      remarks,

      site_name:
        from_location,

      project_name:
        to_location,

      issued_by:
        approved_by,

      reference_type:
        "stock_transfer",
    });

    // =====================================
    // IN ENTRY
    // =====================================

    await createInventoryTransaction({

      company_id,

      movement_type:
        MOVEMENT_TYPES.STOCK_TRANSFER_IN,

      item_id,

      item_code,

      item_name,

      category,

      qty:
        finalQty,

      unit,

      stock_effect:
  finalQty,

  location_type:
  "site_transfer",

location_id:
  to_location,

      remarks,

      site_name:
        to_location,

      project_name:
        from_location,

      received_by:
        approved_by,

      reference_type:
        "stock_transfer",
    });

    return {
      success: true
    };

  } catch (error) {

    console.error(
      "transferStock error:",
      error
    );

    throw error;
  }
};
/* ==================================================
   STRICT ITEM VALIDATION
================================================== */

const validateItemIdentity =
  (data = {}) => {

    const itemId =
      String(
        data.item_id || ""
      ).trim();

    if (!itemId) {

      throw new Error(
        "VALID item_id required"
      );
    }

    return itemId;
  };

/* ==================================================
   GET CURRENT STOCK
================================================== */

/* ==================================================
   GET CURRENT STOCK
================================================== */

export const getCurrentStock =
  async (item_id, company_id) => {

    try {

      validateItemIdentity({
        item_id
      });

      if (!company_id) {
        throw new Error("company_id required");
      }

      // =====================================
      // NEW ARCHITECTURE
      // inventory_summary/{item_id}
      // =====================================

      const summaryRef =
        doc(
          db,
          INVENTORY_SUMMARY,
          item_id
        );

      let summarySnap =
        await getDoc(
          summaryRef
        );

      if (summarySnap.exists()) {

        return summarySnap.data();
      }

      // =====================================
      // LEGACY BACKWARD COMPATIBILITY
      // OLD RANDOM DOC IDs
      // company_id filter is mandatory — never an unscoped
      // collection-wide scan (D-243: was a cross-tenant read risk)
      // =====================================

      const legacySnap =
        await getDocs(
          query(
            collection(db, INVENTORY_SUMMARY),
            where("company_id", "==", company_id)
          )
        );

      const legacyDoc =
        legacySnap.docs.find(
          (d) =>

            String(
              d.data()?.item_id || ""
            ).trim() ===

            String(
              item_id || ""
            ).trim()
        );

      if (legacyDoc) {

        return legacyDoc.data();
      }

      // =====================================
      // NO STOCK
      // =====================================

      return {

        qty: 0,

        defective_stock: 0,

        scrap_stock: 0,
      };

    } catch (error) {

      console.error(
        "getCurrentStock error:",
        error
      );

      throw error;
    }
  };

/* ==================================================
   VALIDATE AVAILABLE STOCK
================================================== */

export const validateAvailableStock =
  async (
    item_id,
    required_qty,
    company_id
  ) => {

    try {

      validateItemIdentity({
        item_id
      });

      if (!company_id) {
        throw new Error(
          "company_id required"
        );
      }

      const qty =
        Number(
          required_qty || 0
        );

      if (qty <= 0) {

        throw new Error(
          "Valid qty required"
        );
      }

      const stockData =
        await getCurrentStock(
          item_id,
          company_id
        );

      const available =
        Number(
          stockData.qty || stockData.current_stock || 0
        );

      if (
        available < qty
      ) {

        return {

          success: false,

          available_stock:
            available,

          required_stock:
            qty,

          message:
            `Insufficient stock. Available: ${available}`,
        };
      }

      return {

        success: true,

        available_stock:
          available,

        required_stock:
          qty,

        message:
          "Stock available",
      };

    } catch (error) {

      console.error(
        "validateAvailableStock error:",
        error
      );

      throw error;
    }
  };

  /* ==================================================
   DETERMINISTIC TRANSACTION BUILDER
================================================== */

const buildInventoryTransaction = (
  data = {}
) => {

  const qty =
    Number(data.qty || 0);

  const stockEffect =
    Number(
      data.stock_effect || 0
    );

  return {

    company_id:
      data.company_id || "",

    movement_type:
      data.movement_type || "",

    // =====================================
    // ITEM
    // =====================================

    item_id:
      data.item_id || "",

    item_code:
      data.item_code || "",

    item_name:
      data.item_name || "",

    category:
      data.category || "",

    unit:
      data.unit || "",

    // =====================================
    // STOCK
    // =====================================

    qty,

    stock_effect:
      stockEffect,

    stock_before:
      Number(
        data.stock_before || 0
      ),

    stock_after:
      Number(
        data.stock_after || 0
      ),

    // =====================================
    // LOCATION
    // =====================================

    location_type:
      data.location_type || "",

    location_id:
      data.location_id || "",

    site_id:
      data.site_id || "",

    site_name:
      data.site_name || "",

    project_id:
      data.project_id || "",

    project_name:
      data.project_name || "",

    // =====================================
    // USERS
    // =====================================

    technician_id:
      data.technician_id || "",

    technician_name:
      data.technician_name || "",

    issued_by:
      data.issued_by || "",

    // D-299: display names next to the uids — ledgers show these.
    issued_by_name:
      data.issued_by_name || "",

    received_by:
      data.received_by || "",

    received_by_name:
      data.received_by_name || "",

    approved_by:
      data.approved_by || "",

    // =====================================
    // REFERENCES
    // =====================================

    reference_id:
      data.reference_id || "",

    reference_type:
      data.reference_type || "",

    reconciliation_group_id:
      data.reconciliation_group_id || "",

    // =====================================
    // META
    // =====================================

    remarks:
      data.remarks || "",

    deterministic_key:
      data.deterministic_key ||

      `${Date.now()}_${
        data.movement_type || ""
      }_${
        data.item_id || ""
      }`,

    created_at:
      serverTimestamp(),
  };
};



/* ==================================================
   CREATE INVENTORY TRANSACTION
================================================== */

export const createInventoryTransaction =
  async (data) => {

    try {

      const itemId =
        validateItemIdentity(
          data
        );

      if (
        !data.company_id
      ) {

        throw new Error(
          "company_id required"
        );
      }

      // =====================================
      // CENTRALIZED SST PAYLOAD
      // =====================================

      const payload =
        buildInventoryTransaction({

          ...data,

          item_id:
            itemId,
        });

      // =====================================
      // WRITE TRANSACTION
      // =====================================

      const txRef =
        await addDoc(

          collection(
            db,
            INVENTORY_TRANSACTIONS
          ),

          payload
        );

      return {

        success: true,

        transaction_id:
          txRef.id,
      };

    } catch (error) {

      console.error(
        "createInventoryTransaction error:",
        error
      );

      throw error;
    }
  };

/* ==================================================
   CENTRALIZED STOCK MUTATION ENGINE
================================================== */

export const updateInventorySummary =
  async ({
    company_id,
    item_id,
    item_code,
    item_name,
    category,
    unit,
    stock_change = 0,
    defective_change = 0,
    scrap_change = 0,
    last_rate = null,        // ← NEW: GRN rate per unit
  }) => {

    try {

      const validatedItemId =
        validateItemIdentity({
          item_id
        });

      if (
        !company_id
      ) {

        throw new Error(
          "company_id required"
        );
      }

      const summaryRef =
        doc(
          db,
          INVENTORY_SUMMARY,
          validatedItemId
        );

      await runTransaction(
        db,
        async (
          transaction
        ) => {

          const summarySnap =
            await transaction.get(
              summaryRef
            );

          // =====================================
          // CREATE NEW SUMMARY
          // =====================================

          if (
            !summarySnap.exists()
          ) {

            const initialQty =
              Number(
                stock_change || 0
              );

            if (
              initialQty < 0
            ) {

              throw new Error(
                "Cannot create negative stock"
              );
            }

            transaction.set(
              summaryRef,
              {
                company_id,
                item_id:         validatedItemId,
                item_code:       item_code || "",
                item_name:       item_name || "",
                category:        category  || "",
                unit:            unit      || "",
                qty:             initialQty,
                defective_stock: Number(defective_change || 0),
                scrap_stock:     Number(scrap_change     || 0),
                ...(last_rate !== null && last_rate > 0
                  ? { last_rate, last_rate_updated: serverTimestamp() }
                  : {}),
                created_at: serverTimestamp(),
                updated_at: serverTimestamp(),
              }
            );

            return;
          }

          // =====================================
          // EXISTING SUMMARY
          // =====================================

          const current =
            summarySnap.data();

          const currentQty =
            Number(
              current.qty || 0
            );

          const currentDefective =
            Number(
              current.defective_stock || 0
            );

          const currentScrap =
            Number(
              current.scrap_stock || 0
            );

          const nextQty =

            currentQty +

            Number(
              stock_change || 0
            );

          const nextDefective =

            currentDefective +

            Number(
              defective_change || 0
            );

          const nextScrap =

            currentScrap +

            Number(
              scrap_change || 0
            );

          // =====================================
          // NEGATIVE STOCK PROTECTION
          // =====================================

          if (
            nextQty < 0
          ) {

            throw new Error(
              `Negative stock blocked for ${validatedItemId}`
            );
          }

          if (
            nextDefective < 0
          ) {

            throw new Error(
              `Negative defective stock blocked for ${validatedItemId}`
            );
          }

          if (
            nextScrap < 0
          ) {

            throw new Error(
              `Negative scrap stock blocked for ${validatedItemId}`
            );
          }

          // =====================================
          // SAFE UPDATE
          // =====================================

          transaction.update(
            summaryRef,
            {
              qty:             nextQty,
              defective_stock: nextDefective,
              scrap_stock:     nextScrap,
              updated_at:      serverTimestamp(),
              // Only update last_rate if a valid rate is provided (GRN sets it)
              ...(last_rate !== null && last_rate > 0
                ? { last_rate, last_rate_updated: serverTimestamp() }
                : {}),
            }
          );
        }
      );

      return {

        success: true,

        item_id:
          validatedItemId,
      };

    } catch (error) {

      console.error(
        "updateInventorySummary error:",
        error
      );

      throw error;
    }
  };

/* ==================================================
   SITE-WISE STOCK CACHE (Site-wise Stock Tracking, Phase 1)
================================================== */
// CACHE ONLY — replayable from inventory_transactions.site_id, exactly
// the same relationship inventory_summary already has to
// inventory_transactions (Constitution §2, Cache Rule). Doc ID =
// `${site_id}_${item_id}` — O(1) direct lookup per site+item, same
// addressing pattern inventory_summary uses (doc ID = item_id).
//
// In THIS phase, only called with a POSITIVE stock_change (material
// physically arriving at a site via MR_ISSUE increases that site's
// stock — central inventory_summary is deducted separately, unaffected
// by this). The negative-stock guard below is defensive for a FUTURE
// phase (site-to-site transfer OUT, on-site consumption tracking) —
// nothing in this phase ever calls it with a negative change.
export const updateSiteInventorySummary =
  async ({
    company_id,
    site_id,
    site_name,
    item_id,
    item_code,
    item_name,
    category,
    unit,
    stock_change = 0,
  }) => {

    try {

      const validatedItemId =
        validateItemIdentity({ item_id });

      if (!company_id) {
        throw new Error("company_id required");
      }
      if (!site_id) {
        throw new Error("site_id required");
      }

      const docId = `${site_id}_${validatedItemId}`;
      const summaryRef = doc(db, SITE_INVENTORY_SUMMARY, docId);

      await runTransaction(db, async (transaction) => {
        const summarySnap = await transaction.get(summaryRef);

        if (!summarySnap.exists()) {
          const initialQty = Number(stock_change || 0);

          if (initialQty < 0) {
            throw new Error("Cannot create negative site stock");
          }

          transaction.set(summaryRef, {
            company_id,
            site_id,
            site_name:  site_name || "",
            item_id:    validatedItemId,
            item_code:  item_code || "",
            item_name:  item_name || "",
            category:   category  || "",
            unit:       unit      || "",
            qty:        initialQty,
            created_at: serverTimestamp(),
            updated_at: serverTimestamp(),
          });

          return;
        }

        const current   = summarySnap.data();
        const currentQty = Number(current.qty || 0);
        const nextQty    = currentQty + Number(stock_change || 0);

        if (nextQty < 0) {
          throw new Error(
            `Negative site stock blocked for ${validatedItemId} at site ${site_id}`
          );
        }

        transaction.update(summaryRef, {
          qty:        nextQty,
          site_name:  site_name || current.site_name || "", // keep display name fresh
          updated_at: serverTimestamp(),
        });
      });

      return { success: true, site_id, item_id: validatedItemId };

    } catch (error) {

      console.error(
        "updateSiteInventorySummary error:",
        error
      );

      throw error;
    }
  };

/* ==================================================
   SITE-TO-SITE STOCK TRANSFER (Site-wise Stock Tracking, Phase 2)
================================================== */
// ACCEPTANCE_REQUIRED pattern, mirroring the asset-transfer engine
// (assetTransactionService.js) — with one deliberate, confirmed
// difference: the SENDER's site stock deducts IMMEDIATELY on transfer
// creation (material has physically left that site), not on
// acceptance. Only the RECEIVER's credit waits for acceptance. This
// mirrors the asset engine's accountability-for-in-transit reasoning,
// adapted for a quantity instead of a single trackable asset.
//
// site_stock_transfers is its own collection, NOT a reuse of the
// pre-existing transferStock()/STOCK_TRANSFER_OUT-IN pair above — that
// function only tags inventory_transactions.site_name as a free-text
// audit label and never touches site_inventory_summary or any real
// per-site balance. Confirmed distinct domains: transferStock() is a
// central-inventory relocation record; this is a movement between two
// site_inventory_summary rows. Single Source of Truth rule — these two
// write-paths must never be merged.
const SITE_STOCK_TRANSFERS = "site_stock_transfers";

const getNextTransferNumber = async (company_id) => {
  if (!company_id) throw new Error("company_id required");

  const counterRef = doc(db, "system_counters", `stxn_${company_id}`);

  return runTransaction(db, async (transaction) => {
    const counterSnap = await transaction.get(counterRef);

    let nextNumber;

    if (!counterSnap.exists()) {
      nextNumber = 1;
      transaction.set(counterRef, {
        counter_type: "site_stock_transfer",
        company_id,
        last_number: nextNumber,
        updated_at: serverTimestamp(),
      });
    } else {
      nextNumber = Number(counterSnap.data().last_number || 0) + 1;
      transaction.update(counterRef, {
        last_number: nextNumber,
        updated_at: serverTimestamp(),
      });
    }

    return `STXN-${String(nextNumber).padStart(4, "0")}`;
  });
};

// Sender-side creation — deducts sender's site stock IMMEDIATELY, inside
// the SAME atomic transaction as the stock-check + transfer-doc creation
// (so a concurrent second transfer of the same item from the same site
// can never race past the check). Receiver's site stock is NOT touched
// here — only on acceptSiteStockTransfer() below.
export const createSiteStockTransfer = async ({
  company_id,
  from_site_id,
  from_site_name,
  to_site_id,
  to_site_name,
  item_id,
  item_code,
  item_name,
  unit,
  qty,
  requested_by,
  requested_by_name = "",
  remarks = "",
}) => {
  try {
    const validatedItemId = validateItemIdentity({ item_id });

    if (!company_id)   throw new Error("company_id required");
    if (!from_site_id) throw new Error("from_site_id required");
    if (!to_site_id)   throw new Error("to_site_id required");
    if (from_site_id === to_site_id) {
      throw new Error("Source and destination site cannot be the same");
    }
    if (!requested_by) throw new Error("requested_by required");

    const finalQty = Number(qty || 0);
    if (finalQty <= 0) throw new Error("Valid transfer qty required");

    const transfer_no = await getNextTransferNumber(company_id);

    const fromDocId    = `${from_site_id}_${validatedItemId}`;
    const fromRef       = doc(db, SITE_INVENTORY_SUMMARY, fromDocId);
    const transferRef   = doc(collection(db, SITE_STOCK_TRANSFERS));

    await runTransaction(db, async (transaction) => {
      const fromSnap = await transaction.get(fromRef);

      const currentQty = fromSnap.exists() ? Number(fromSnap.data().qty || 0) : 0;
      if (currentQty < finalQty) {
        throw new Error(
          `Insufficient stock at source site. Available: ${currentQty} ${unit || ""}`
        );
      }

      transaction.update(fromRef, {
        qty: currentQty - finalQty,
        updated_at: serverTimestamp(),
      });

      transaction.set(transferRef, {
        company_id,
        transfer_no,
        from_site_id,
        from_site_name: from_site_name || "",
        to_site_id,
        to_site_name:   to_site_name   || "",
        item_id:   validatedItemId,
        item_code: item_code || "",
        item_name: item_name || "",
        unit:      unit      || "",
        qty:       finalQty,
        status: "pending_acceptance",
        requested_by,
        requested_by_name,
        remarks,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
    });

    await createInventoryTransaction({
      company_id,
      movement_type: MOVEMENT_TYPES.SITE_TRANSFER_OUT,
      item_id:   validatedItemId,
      item_code: item_code || "",
      item_name: item_name || "",
      unit:      unit      || "",
      qty:       finalQty,
      stock_effect:   -finalQty,
      location_type:  "site",
      location_id:    from_site_id,
      site_id:        from_site_id,
      site_name:      from_site_name || "",
      remarks,
      issued_by:      requested_by,
      reference_id:   transferRef.id,
      reference_type: "site_stock_transfer",
    });

    return { success: true, transfer_id: transferRef.id, transfer_no };

  } catch (error) {
    console.error("createSiteStockTransfer error:", error);
    throw error;
  }
};

// Receiver confirms — credits the RECEIVING site's stock and finalizes
// the transfer. Sender's stock was already deducted at creation time —
// this step only ever adds, never subtracts.
export const acceptSiteStockTransfer = async ({
  transfer_id,
  accepted_by,
  accepted_by_name = "",
  company_id,
}) => {
  try {
    if (!transfer_id) throw new Error("transfer_id required");
    if (!company_id)  throw new Error("company_id required");

    const transferRef  = doc(db, SITE_STOCK_TRANSFERS, transfer_id);
    const transferSnap = await getDoc(transferRef);
    if (!transferSnap.exists()) throw new Error("Transfer not found");

    const transfer = transferSnap.data();
    if (transfer.company_id !== company_id) throw new Error("Unauthorized company access");
    if (transfer.status !== "pending_acceptance") {
      throw new Error(`Transfer is already ${transfer.status} — cannot accept`);
    }

    await updateSiteInventorySummary({
      company_id,
      site_id:   transfer.to_site_id,
      site_name: transfer.to_site_name,
      item_id:   transfer.item_id,
      item_code: transfer.item_code,
      item_name: transfer.item_name,
      unit:      transfer.unit,
      stock_change: Number(transfer.qty || 0),
    });

    await createInventoryTransaction({
      company_id,
      movement_type: MOVEMENT_TYPES.SITE_TRANSFER_IN,
      item_id:   transfer.item_id,
      item_code: transfer.item_code,
      item_name: transfer.item_name,
      unit:      transfer.unit,
      qty:       transfer.qty,
      stock_effect:  Number(transfer.qty || 0),
      location_type: "site",
      location_id:   transfer.to_site_id,
      site_id:       transfer.to_site_id,
      site_name:     transfer.to_site_name,
      received_by:    accepted_by,
      reference_id:   transfer_id,
      reference_type: "site_stock_transfer",
    });

    await updateDoc(transferRef, {
      status: "accepted",
      accepted_by,
      accepted_by_name,
      accepted_at: serverTimestamp(),
      updated_at:  serverTimestamp(),
    });

    return { success: true };

  } catch (error) {
    console.error("acceptSiteStockTransfer error:", error);
    throw error;
  }
};

// Shared reversal helper — reject (receiver declines) and cancel
// (any sm/owner/manager/store aborts) both return the sender's site
// stock the exact same way: a NEW positive stock_change back at the
// origin site, never an edit of the original deduction (Golden Rule —
// transactions never edit, only reversal entries).
const reverseSiteStockTransfer = async ({
  transfer_id,
  company_id,
  actor_id,
  actor_name,
  reason,
  resultStatus,      // "rejected" | "cancelled"
  actorField,         // "rejected_by" | "cancelled_by"
  actorNameField,     // "rejected_by_name" | "cancelled_by_name"
  reasonField,        // "rejection_reason" | "cancellation_reason"
  timestampField,     // "rejected_at" | "cancelled_at"
  extraFields = {},
}) => {
  if (!transfer_id) throw new Error("transfer_id required");
  if (!company_id)  throw new Error("company_id required");
  if (!reason || !reason.trim()) throw new Error("Reason is mandatory");

  const transferRef  = doc(db, SITE_STOCK_TRANSFERS, transfer_id);
  const transferSnap = await getDoc(transferRef);
  if (!transferSnap.exists()) throw new Error("Transfer not found");

  const transfer = transferSnap.data();
  if (transfer.company_id !== company_id) throw new Error("Unauthorized company access");
  if (transfer.status !== "pending_acceptance") {
    throw new Error(
      `Transfer is already ${transfer.status} — cannot ${resultStatus === "rejected" ? "reject" : "cancel"}`
    );
  }

  await updateSiteInventorySummary({
    company_id,
    site_id:   transfer.from_site_id,
    site_name: transfer.from_site_name,
    item_id:   transfer.item_id,
    item_code: transfer.item_code,
    item_name: transfer.item_name,
    unit:      transfer.unit,
    stock_change: Number(transfer.qty || 0),
  });

  await createInventoryTransaction({
    company_id,
    movement_type: MOVEMENT_TYPES.SITE_TRANSFER_REVERSED,
    item_id:   transfer.item_id,
    item_code: transfer.item_code,
    item_name: transfer.item_name,
    unit:      transfer.unit,
    qty:       transfer.qty,
    stock_effect:  Number(transfer.qty || 0),
    location_type: "site",
    location_id:   transfer.from_site_id,
    site_id:       transfer.from_site_id,
    site_name:     transfer.from_site_name,
    remarks: reason,
    received_by:    actor_id,
    reference_id:   transfer_id,
    reference_type: "site_stock_transfer",
  });

  await updateDoc(transferRef, {
    status: resultStatus,
    [actorField]:     actor_id,
    [actorNameField]: actor_name || "",
    [reasonField]:    reason.trim(),
    [timestampField]: serverTimestamp(),
    updated_at:       serverTimestamp(),
    ...extraFields,
  });

  return { success: true };
};

export const rejectSiteStockTransfer = async ({
  transfer_id,
  rejected_by,
  rejected_by_name = "",
  rejection_reason,
  company_id,
}) => reverseSiteStockTransfer({
  transfer_id,
  company_id,
  actor_id:   rejected_by,
  actor_name: rejected_by_name,
  reason:     rejection_reason,
  resultStatus:   "rejected",
  actorField:     "rejected_by",
  actorNameField: "rejected_by_name",
  reasonField:    "rejection_reason",
  timestampField: "rejected_at",
});

export const cancelSiteStockTransfer = async ({
  transfer_id,
  cancelled_by,
  cancelled_by_name = "",
  cancellation_reason,
  company_id,
}) => reverseSiteStockTransfer({
  transfer_id,
  company_id,
  actor_id:   cancelled_by,
  actor_name: cancelled_by_name,
  reason:     cancellation_reason,
  resultStatus:   "cancelled",
  actorField:     "cancelled_by",
  actorNameField: "cancelled_by_name",
  reasonField:    "cancellation_reason",
  timestampField: "cancelled_at",
  extraFields: { cancelled_by_role: "manager" },
});

/* ==================================================
   RECEIVE GRN
================================================== */

export const receiveGRN =
  async (data) => {
    try {
      validateItemIdentity(data);
      const qty = Number(data.qty || 0);
      if (qty <= 0) {
        throw new Error("Valid GRN qty required");
      }

      // ✅ LONG TERM FIX: inventory_summary doc ID resolve karo
      // Priority: item_id → legacy query fallback
      const itemId = String(data.item_id || "").trim();

      // Check 1: doc exists with item_id as doc ID
      const directRef  = doc(db, INVENTORY_SUMMARY, itemId);
      const directSnap = await getDoc(directRef);

      let resolvedItemId = itemId; // default

      if (!directSnap.exists()) {
        // Check 2: legacy — query by item_id field
        const legacySnap = await getDocs(query(
          collection(db, INVENTORY_SUMMARY),
          where("company_id", "==", data.company_id || ""),
          where("item_id",    "==", itemId)
        ));

        if (!legacySnap.empty) {
          // Found legacy doc — use its Firestore doc ID
          resolvedItemId = legacySnap.docs[0].id;
          console.log(`[receiveGRN] Legacy doc found: ${itemId} → doc ID: ${resolvedItemId}`);
        } else {
          // Check 3: query by item_code
          const codeSnap = await getDocs(query(
            collection(db, INVENTORY_SUMMARY),
            where("company_id", "==", data.company_id || ""),
            where("item_code",  "==", data.item_code  || "")
          ));
          if (!codeSnap.empty) {
            resolvedItemId = codeSnap.docs[0].id;
            console.log(`[receiveGRN] Code match found: ${data.item_code} → doc ID: ${resolvedItemId}`);
          } else {
            // New item — use item_id as doc ID (correct long-term behavior)
            console.log(`[receiveGRN] New inventory_summary will be created: ${itemId}`);
          }
        }
      }

      await updateInventorySummary({
        company_id:   data.company_id,
        item_id:      resolvedItemId,  // ✅ resolved doc ID
        item_code:    data.item_code,
        item_name:    data.item_name,
        category:     data.category,
        unit:         data.unit,
        stock_change: qty,
        last_rate:    data.rate ? Number(data.rate) : null,
      });

      await createInventoryTransaction({
        ...data,
        item_id:       resolvedItemId, // ✅ resolved doc ID
        movement_type: MOVEMENT_TYPES.GRN_IN,
        stock_effect:  qty,
      });

      console.log(`[receiveGRN] ✅ Stock updated: ${data.item_name} +${qty} (doc: ${resolvedItemId})`);

      return {
        success: true,
        message: "GRN received successfully",
      };

    } catch (error) {
      console.error("receiveGRN error:", error);
      throw error;
    }
  };

/* ==================================================
   RECEIVE GRN AT SITE (Direct Site Purchase — Priority 4)
   Sibling of receiveGRN() above, deliberately NOT shared — writes to a
   different collection and must never be merged (Single Source of
   Truth rule). NEVER touches inventory_summary — central stock stays
   untouched because material never arrived at the store (Constitution
   §5 amendment).
================================================== */

export const receiveGRNAtSite =
  async (data) => {
    try {
      validateItemIdentity(data);
      const qty = Number(data.qty || 0);
      if (qty <= 0) {
        throw new Error("Valid GRN qty required");
      }
      if (!data.site_id) {
        throw new Error("site_id required for site-scoped GRN");
      }

      await updateSiteInventorySummary({
        company_id: data.company_id,
        site_id:    data.site_id,
        site_name:  data.site_name || "",
        item_id:    data.item_id,
        item_code:  data.item_code || "",
        item_name:  data.item_name,
        category:   data.category || "",
        unit:       data.unit || "",
        stock_change: qty,
      });

      await createInventoryTransaction({
        ...data,
        movement_type: MOVEMENT_TYPES.SITE_GRN_IN,
        stock_effect:  qty,
        location_type: "site",
        location_id:   data.site_id,
      });

      console.log(`[receiveGRNAtSite] ✅ Site stock updated: ${data.item_name} +${qty} at site ${data.site_id}`);

      return {
        success: true,
        message: "Site GRN received successfully",
      };

    } catch (error) {
      console.error("receiveGRNAtSite error:", error);
      throw error;
    }
  };

/* ==================================================
   ISSUE AGAINST MR
================================================== */

/* ==================================================
   ISSUE AGAINST MR
================================================== */

export const issueAgainstMR =
  async (data) => {

    try {

      // =========================================
      // STRICT ITEM VALIDATION
      // =========================================

      const validatedItemId =
        validateItemIdentity(data);

      if (
        !data.company_id
      ) {

        throw new Error(
          "company_id required"
        );
      }

      const qty =
        Number(data.qty || 0);

      if (qty <= 0) {

        throw new Error(
          "Valid issue qty required"
        );
      }

      // =========================================
      // STOCK VALIDATION
      // =========================================

      const validation =
        await validateAvailableStock(
          validatedItemId,
          qty,
          data.company_id || ""
        );

      if (
        !validation.success
      ) {

        throw new Error(
          validation.message
        );
      }

      // =========================================
      // CENTRALIZED STOCK DEDUCTION
      // =========================================

      await updateInventorySummary({

        company_id:
          data.company_id,

        item_id:
          validatedItemId,

        item_code:
          data.item_code || "",

        item_name:
          data.item_name || "",

        category:
          data.category || "",

        unit:
          data.unit || "",

        stock_change:
          -qty,
      });

      // =========================================
      // INVENTORY LEDGER ENTRY
      // =========================================

      await createInventoryTransaction({

        ...data,

        item_id:
          validatedItemId,

        movement_type:
          MOVEMENT_TYPES.MR_ISSUE,

        stock_effect:
          -qty,
      });

      // =========================================
      // MATERIAL COST → financial_transactions
      // (D-186 prerequisite fix — non-blocking,
      // does NOT touch existing behavior above)
      // =========================================

      if (data.project_id) {

        try {

          const stockData =
            await getCurrentStock(
              validatedItemId,
              data.company_id || ""
            );

          const rate =
            Number(stockData.last_rate || 0);

          const amount =
            qty * rate;

          await addDoc(
            collection(db, "financial_transactions"),
            {
              company_id:      data.company_id,
              type:            "expense",
              category:        "material",
              amount,
              project_id:      data.project_id,
              project_name:    data.project_name || "",
              site_id:         data.site_id || "",
              site_name:       data.site_name || "",
              item_id:         validatedItemId,
              item_name:       data.item_name || "",
              qty,
              rate,
              reference_type:  "material_issue",
              reference_id:    `${data.reference_id || ""}_${validatedItemId}`,
              status:          "completed",
              created_by:      data.issued_by || "",
              created_by_name: data.issued_by_name || data.issued_by || "",
              created_at:      serverTimestamp(),
              updated_at:      serverTimestamp(),
            }
          );

        } catch (finErr) {

          console.error(
            "issueAgainstMR — financial_transactions write failed (non-blocking):",
            finErr
          );
        }
      }

      // =========================================
      // SITE-WISE STOCK CACHE (Site-wise Stock Tracking, Phase 1)
      // Non-blocking, additive only — mirrors the financial_transactions
      // block above exactly: failure here NEVER rolls back or blocks the
      // real issue (central stock deduction + ledger entry above already
      // succeeded and stay authoritative regardless). Only runs when a
      // site_id was actually resolved by the caller — an MR issue with no
      // resolvable site (e.g. a service-only MR with no project/site
      // link) simply gets no site-stock entry, same detect-and-fallback
      // discipline as every other legacy/missing-data case in this
      // codebase — never a silent guess.
      // =========================================

      if (data.site_id) {

        try {

          await updateSiteInventorySummary({
            company_id: data.company_id,
            site_id:    data.site_id,
            site_name:  data.site_name || "",
            item_id:    validatedItemId,
            item_code:  data.item_code || "",
            item_name:  data.item_name || "",
            category:   data.category || "",
            unit:       data.unit || "",
            stock_change: qty,
          });

        } catch (siteErr) {

          console.error(
            "issueAgainstMR — site_inventory_summary write failed (non-blocking):",
            siteErr
          );
        }
      }

      // =========================================
      // IMPORTANT:
      // DO NOT UPDATE material_requests HERE
      //
      // StoreMRFDetail.js
      // is now SINGLE SOURCE OF TRUTH
      // for:
      //
      // - issued_qty
      // - remaining_qty
      // - item_status
      // - MRF status
      //
      // This prevents:
      // - stale overwrite
      // - double issue
      // - race condition
      // - duplicate writes
      // =========================================

      return {

        success: true,

        message:
          "MR issue successful",

        item_id:
          validatedItemId,

        issued_qty:
          qty,
      };

    } catch (error) {

      console.error(
        "issueAgainstMR error:",
        error
      );

      throw error;
    }
  };

/* ==================================================
   DIRECT ISSUE
================================================== */

export const directIssue =
  async (data) => {

    try {

      validateItemIdentity(
        data
      );

      const qty =
        Number(
          data.qty || 0
        );

      if (qty <= 0) {

        throw new Error(
          "Valid qty required"
        );
      }

      const validation =
        await validateAvailableStock(
          data.item_id,
          qty,
          data.company_id || ""
        );

      if (
        !validation.success
      ) {

        throw new Error(
          validation.message
        );
      }

      await updateInventorySummary({

        company_id:
          data.company_id,

        item_id:
          data.item_id,

        item_code:
          data.item_code,

        item_name:
          data.item_name,

        category:
          data.category,

        unit:
          data.unit,

        stock_change:
          -qty,
      });

      await createInventoryTransaction({

        ...data,

        movement_type:
          MOVEMENT_TYPES.DIRECT_ISSUE,

        stock_effect:
          -qty,
      });

      return {

        success: true,

        message:
          "Direct issue successful",
      };

    } catch (error) {

      console.error(
        "directIssue error:",
        error
      );

      throw error;
    }
  };

/* ==================================================
   RETURN MATERIAL
================================================== */

export const returnMaterial =
  async (data) => {

    try {

      validateItemIdentity(
        data
      );

      const qty =
        Number(
          data.qty || 0
        );

      if (qty <= 0) {

        throw new Error(
          "Valid qty required"
        );
      }

      await updateInventorySummary({

        company_id:
          data.company_id,

        item_id:
          data.item_id,

        item_code:
          data.item_code,

        item_name:
          data.item_name,

        category:
          data.category,

        unit:
          data.unit,

        stock_change:
          qty,
      });

      await createInventoryTransaction({

        ...data,

        movement_type:
          MOVEMENT_TYPES.RETURN_UNUSED,

        stock_effect:
          qty,
      });

      return {

        success: true,

        message:
          "Material returned successfully",
      };

    } catch (error) {

      console.error(
        "returnMaterial error:",
        error
      );

      throw error;
    }
  };

/* ==================================================
   DEFECTIVE RETURN
================================================== */

export const defectiveReturn =
  async (data) => {

    try {

      validateItemIdentity(
        data
      );

      const qty =
        Number(
          data.qty || 0
        );

      if (qty <= 0) {

        throw new Error(
          "Valid qty required"
        );
      }

      await updateInventorySummary({

        company_id:
          data.company_id,

        item_id:
          data.item_id,

        item_code:
          data.item_code,

        item_name:
          data.item_name,

        category:
          data.category,

        unit:
          data.unit,

        defective_change:
          qty,
      });

      await createInventoryTransaction({

        ...data,

        movement_type:
          MOVEMENT_TYPES.RETURN_DEFECTIVE,

        stock_effect:
          0,
      });

      return {

        success: true,

        message:
          "Defective stock updated",
      };

    } catch (error) {

      console.error(
        "defectiveReturn error:",
        error
      );

      throw error;
    }
  };